import { commandsStore } from "@/store/commandsStore";
import { headerBiddingStore } from "@/store/headerBiddingStore";
import { isAuctionModule, isTriggerAuctionCommand } from "@/ts/guards";
import type {
	AuctionControllerConfig,
	AuctionControllerModule,
	ModuleConfig,
} from "@/ts/module";
import type {
	DisplaySlotConfig,
	Exactly,
	SlotTargeting,
	TriggerAuctionCommand,
	VideoSlotConfig,
} from "@/ts/types";
import { markTiming } from "@/utils/commercialDebug";
import { getModules } from "@/utils/modules";

export class AuctionController
	implements Exactly<AuctionControllerModule, AuctionController>
{
	private config: AuctionControllerConfig | undefined;
	name = "AuctionController" as const;

	constructor(auctionConfig: ModuleConfig) {
		if ("defaultTimeout" in auctionConfig) {
			this.config = auctionConfig;
		}
	}

	async init(): Promise<void> {
		const { selectors } = commandsStore.getState();
		// Subscribe to Trigger Auction command
		commandsStore.subscribe(
			selectors.selectTriggerAuctionCommand,
			(selectedCmd) => handleAuctionCmd(this.run, selectedCmd),
		);
	}

	async run(
		slotConfigs: (DisplaySlotConfig | VideoSlotConfig)[],
		callback: (
			slotTargeting: Record<string, SlotTargeting> | undefined,
		) => void,
		timeout = this.config?.defaultTimeout || 2000,
	) {
		const { auctions, actions, selectors, vendorNames } =
			headerBiddingStore.getState();

		const auctionId = Object.keys(auctions).length;
		actions.addAuctionToStore(auctionId, vendorNames);

		const vendorModules = await getModules(vendorNames);
		const vendors = vendorModules.filter(isAuctionModule);

		headerBiddingStore.subscribe(
			selectors.allVendorAuctionsDone(auctionId),
			(allDone) => allDone && handleAuctionCallback(auctionId, callback),
		);
		if (vendors.length === 0) {
			handleAuctionCallback(auctionId, callback);
			return;
		}
		const args = [auctionId, slotConfigs, timeout] as const;
		for (const vendor of vendors) {
			vendor.run(...args);
			const slots = slotConfigs.map((slot) => slot.slotId);
			markTiming(
				`${vendor.name} auction started for : 
        auctionId: ${auctionId} 
        slots: ${slots} 
        timeout: ${args[2]} `,
			);
		}

		setTimeout(() => {
			const { auctions } = headerBiddingStore.getState();
			const isAuctionComplete = auctions[auctionId].done;

			// dont call callback a second time if the happy path already called it
			if (!isAuctionComplete) {
				handleAuctionCallback(auctionId, callback);
			}
		}, timeout);
	}
}

const handleAuctionCallback = (
	auctionId: number,
	callback: (slotTargeting: Record<string, SlotTargeting> | undefined) => void,
) => {
	const { actions, selectors } = headerBiddingStore.getState();
	actions.setAuctionDone(auctionId);
	const slotTargeting = selectors.getSlotTargeting(auctionId);
	callback(slotTargeting);
	markTiming(`auction callback called for auctionId: ${auctionId}`);
};

const handleAuctionCmd = (
	run: (
		slotConfigs: (DisplaySlotConfig | VideoSlotConfig)[],
		callback: () => void,
		timeout: number,
	) => void,
	selectedCmd?: TriggerAuctionCommand,
) => {
	if (selectedCmd && isTriggerAuctionCommand(selectedCmd)) {
		const { slots, timeout, callback } = selectedCmd;
		run(slots, callback, timeout);
	}
};
